// Minimal CSV export helpers for downloading the data shown in a dashboard.
// A cell is quoted only when it contains a comma, quote, or newline (internal
// quotes doubled, per RFC 4180). Rows are CRLF-joined and a UTF-8 BOM is
// prepended so Excel renders non-ASCII (e.g. Norwegian ø/æ/å in area names)
// correctly.

export type Cell = string | number | null | undefined;

// UTF-8 byte-order mark (U+FEFF). Built via char code to keep the source
// all-ASCII (no invisible character in the file).
const BOM = String.fromCharCode(0xfeff);

function csvCell(v: Cell): string {
  const s = v == null ? "" : String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function toCsv(rows: Cell[][]): string {
  return rows.map((r) => r.map(csvCell).join(",")).join("\r\n");
}

/** Build a CSV from `rows` (header row first) and trigger a browser download. */
export function downloadCsv(filename: string, rows: Cell[][]): void {
  const blob = new Blob([BOM + toCsv(rows)], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** `<prefix>-<slugified label>-<YYYYMMDD-HHMM>.csv` (local time). */
export function csvFilename(prefix: string, label: string): string {
  const slug =
    label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40) || "export";
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  const stamp = `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}`;
  return `${prefix}-${slug}-${stamp}.csv`;
}
